import { Building2, LogOut, Settings, Sparkles } from "lucide-react";
import { createElement } from "react";
import type { BootstrapPayload, Command } from "../../core";
import { command } from "../../core";
import type { Candidate, CommandPaletteSource } from "../types";

type Dispatch = (cmd: Command) => Promise<unknown>;

/**
 * Quick-actions source — the "landing page" of the command palette. Emits a
 * small, static set of contextual entries (assistant, settings, org switch,
 * sign-out) when the user has typed nothing yet. As soon as the user types ANY
 * character the source goes silent: that keeps the palette's typed-search
 * experience clean (apps + menus + actions own it) and reserves the empty
 * state for "here is what you probably wanted to do".
 *
 * This source has no `prefix` — the engine still won't fan out to it on a
 * non-empty query because the source itself returns [] in that case. Wiring
 * (assistant toggle, sign-out, settings, org switch) is best-effort against
 * what the platform CommandBus exposes today; entries with no real handler
 * fall back to console.info so they're visible but non-destructive.
 */
export function buildQuickActionsSource(_boot: BootstrapPayload, dispatch: Dispatch): CommandPaletteSource {
    return {
        key: "quick",
        label: "Quick actions",
        priority: 0,
        // No prefix — this is a general/landing source. The engine itself
        // filters: it's only invoked on the empty-query landing path.
        search(query: string): Candidate[] {
            // Any typed input → stay silent. The landing source is strictly
            // for the empty state.
            if (query && query.trim().length > 0) return [];

            const out: Candidate[] = [];

            out.push({
                id: "quick:assistant",
                sourceKey: "quick",
                title: "Open assistant",
                subtitle: "Toggle the AI assistant panel",
                keywords: ["ai", "assistant", "help"],
                icon: createElement(Sparkles, { size: 14 }),
                score: 100,
                onPick: () => {
                    void dispatch(command("TOGGLE_ASSISTANT", {}));
                },
            });

            out.push({
                id: "quick:settings",
                sourceKey: "quick",
                title: "Open Settings",
                subtitle: "Preferences and configuration",
                keywords: ["settings", "preferences", "config"],
                icon: createElement(Settings, { size: 14 }),
                score: 99,
                onPick: () => {
                    // No platform "settings" action is registered today.
                    // Surface a no-op so the entry is visible but harmless;
                    // a future settings action can be wired here by name.
                    console.info("[command-palette] Quick action: Open Settings (no handler wired yet)");
                },
            });

            out.push({
                id: "quick:switch-org",
                sourceKey: "quick",
                title: "Switch organization",
                subtitle: "Open the organization picker",
                keywords: ["organization", "switch", "company", "tenant"],
                icon: createElement(Building2, { size: 14 }),
                score: 98,
                onPick: () => {
                    // The org switcher lives in the workspace header; no
                    // single command toggles it yet. Visible-but-no-op.
                    console.info("[command-palette] Quick action: Switch organization (use header switcher)");
                },
            });

            out.push({
                id: "quick:sign-out",
                sourceKey: "quick",
                title: "Sign out",
                subtitle: "End this session",
                keywords: ["logout", "sign out", "exit"],
                icon: createElement(LogOut, { size: 14 }),
                score: 97,
                onPick: () => {
                    // No platform LOGOUT command is registered today.
                    console.info("[command-palette] Quick action: Sign out (no handler wired yet)");
                },
            });

            return out;
        },
    };
}
